import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split, Stack, withShade} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {AppDrawerLine, LockLine} from '@dropbox/dig-icons/assets';
import ReplitLogo from 'assets/logos/replit.svg?react';
import {EmployeeButton} from 'components/DSYS/EmployeeButton';
import {LabelGroup} from 'components/DSYS/LabelGroup';
import {useEmployee} from 'hooks/useEmployee';
import {t} from 'i18next';
import type {ReactElement} from 'react';
import {TileListRow} from 'views/home/dashboard/TileListRow';

import {AppActionsMenu} from './AppActionsMenu';
import {AppHost, WrappableName} from './AppCover';
import styles from './Apps.module.css';
import {AppThumbnail} from './AppThumbnail';
import {
  getAppDisplayName,
  getAppOwnerLdap,
  getSafeAppLaunchUrl,
  getSafeReplitEditUrl,
  isPinnableApp,
} from './appUtils';
import {CatalogApp, isReplitApp, isReplitDraft} from './catalogApp';
import {isPrivateApp, useAppVisibility} from './useAppVisibility';

const VibeboxBadge = () => (
  <Tooltip title={t('apps_vibebox_badge_tooltip')} placement="top">
    <span className={styles.appNameIndicator} aria-label={t('apps_vibebox_badge_label')}>
      <span className={styles.vibeboxBadge}>{t('apps_vibebox_badge_symbol')}</span>
    </span>
  </Tooltip>
);

const ReplitBadge = () => (
  <Tooltip title={t('apps_replit_badge_tooltip')} placement="top">
    <span className={styles.appNameIndicator} aria-label={t('apps_replit_badge_label')}>
      <ReplitLogo aria-hidden className={styles.replitBadge} />
    </span>
  </Tooltip>
);

const DraftBadge = () => (
  <Tooltip title={t('apps_draft_badge_tooltip')} placement="top">
    <span className={styles.appNameIndicator} aria-label={t('apps_draft_badge_label')}>
      <Text color="subtle" size="small">
        {t('apps_draft_badge_text')}
      </Text>
    </span>
  </Tooltip>
);

const PrivateBadge = () => (
  <Tooltip title={t('apps_private_badge_tooltip')} placement="top">
    <span className={styles.appNameIndicator} aria-label={t('apps_private_badge_label')}>
      <UIIcon src={LockLine} size="small" className={atoms({color: 'Text Subtle'})} />
    </span>
  </Tooltip>
);

const AppNameLabel = ({
  app,
  showName = true,
  showPlatform = true,
  truncate = false,
}: {
  app: CatalogApp;
  showName?: boolean;
  showPlatform?: boolean;
  truncate?: boolean;
}) => {
  const name = getAppDisplayName(app);
  const isPrivate = isPrivateApp(app);
  const isDraft = isReplitDraft(app);
  if (!showName && !isPrivate && !isDraft) return null;

  return (
    <span className={styles.appNameWithBadge}>
      {showName ? (
        <Text isBold className={styles.appNameText} tagName={truncate ? 'div' : 'span'}>
          {truncate ? <Truncate observeParent>{name}</Truncate> : name}
        </Text>
      ) : null}
      {isPrivate ? <PrivateBadge /> : null}
      {showPlatform && app.platform?.['.tag'] === 'vibebox' ? <VibeboxBadge /> : null}
      {showPlatform && isReplitApp(app) ? <ReplitBadge /> : null}
      {isDraft ? <DraftBadge /> : null}
    </span>
  );
};

const CompactCardContent = ({app}: {app: CatalogApp}) => (
  <>
    <Split.Item>
      <span className={styles.appIcon} aria-hidden>
        <UIIcon src={AppDrawerLine} />
      </span>
    </Split.Item>
    <Split.Item width="fill">
      <Stack gap="2">
        <AppNameLabel app={app} />
        <Text className={styles.appDescription} color="subtle" size="small">
          {isReplitApp(app) ? app.description || app.slug : app.slug}
        </Text>
      </Stack>
    </Split.Item>
  </>
);

const ListCardContent = ({app}: {app: CatalogApp}) => (
  <LabelGroup
    withLeftAccessory={
      <AppThumbnail name={getAppDisplayName(app)} screenshot={app.screenshot} siteId={app.id} />
    }
    withText={<AppNameLabel app={app} truncate />}
  />
);

const TileOwner = ({ldap}: {ldap: string}) => {
  const {employee, isLoading} = useEmployee({ldap, throwOnError: false});

  if (isLoading) {
    return (
      <LabelGroup
        withLeftAccessory={<Skeleton.Avatar size="xsmall" />}
        withText={<Skeleton.Text size="small" style={{width: 120}} />}
      />
    );
  }

  if (!employee) {
    return null;
  }

  return (
    <div className={styles.appTileEmployee}>
      <EmployeeButton employee={employee} avatarSize="xsmall" size="small" color="subtle" />
    </div>
  );
};

const AppRow = ({children}: {children: ReactElement}) => (
  <Box paddingY="12">
    <Split gap="12" alignY="center">
      {children}
    </Split>
  </Box>
);

export const AppCatalogCard = ({
  app,
  listLayout = false,
  tileLayout = false,
  withActions = false,
}: {
  app: CatalogApp;
  listLayout?: boolean;
  tileLayout?: boolean;
  withActions?: boolean;
}) => {
  const name = getAppDisplayName(app);
  const accessibleName = isReplitDraft(app)
    ? t('apps_replit_draft_label', {name})
    : isReplitApp(app)
      ? t('apps_replit_app_label', {name})
      : app.platform?.['.tag'] === 'vibebox'
        ? t('apps_vibebox_app_label', {name})
        : isPrivateApp(app)
          ? t('apps_private_app_label', {name})
          : name;
  const launchUrl = getSafeAppLaunchUrl(app);
  const editUrl = getSafeReplitEditUrl(app);
  const ownerLdap = getAppOwnerLdap(app);
  const canDelete = app.platform?.['.tag'] === 'company_os' && Boolean(app.id);
  const {canChangeVisibility} = useAppVisibility(app);
  // Every viewer can pin an App Platform app to home; owner actions need `withActions`.
  const showActions =
    (withActions && Boolean(launchUrl || editUrl || canDelete || canChangeVisibility)) ||
    isPinnableApp(app);
  const actionsMenu = <AppActionsMenu app={app} withManageActions={withActions} />;

  if (tileLayout) {
    const shade = withShade({className: styles.appTileFrame});
    const showStatus = isPrivateApp(app) || isReplitDraft(app);
    const host: AppHost = isReplitApp(app)
      ? 'replit'
      : app.platform?.['.tag'] === 'vibebox'
        ? 'vibebox'
        : 'dropbox';
    const thumbnail = (
      <AppThumbnail
        host={host}
        name={name}
        screenshot={app.screenshot}
        siteId={app.id}
        variant="tile"
      />
    );

    return (
      // The shade lives on a frame just outside the card, so hover reads as a halo behind it.
      <Box className={shade.className} style={shade.style}>
        <Box
          className={[styles.appTile, app.screenshot ? styles.appTileOnImage : '']
            .filter(Boolean)
            .join(' ')}
        >
          {launchUrl ? (
            <a
              aria-label={accessibleName}
              className={styles.appTileLink}
              href={launchUrl}
              onClick={(event) => {
                event.currentTarget.blur();
              }}
              rel="noopener noreferrer"
              target="_blank"
            >
              {thumbnail}
            </a>
          ) : (
            <div className={styles.appTileLink}>{thumbnail}</div>
          )}
          {showStatus ? (
            <div className={styles.appTileStatus}>
              <AppNameLabel app={app} showName={false} showPlatform={false} />
            </div>
          ) : null}
          {/* Name and byline sit on the card, bottom-left; screenshots get a scrim behind them. */}
          <Box className={styles.appTileOverlay} colorScheme={app.screenshot ? 'Dark' : undefined}>
            <span className={styles.appTileName} aria-hidden>
              <WrappableName name={name} />
            </span>
            {ownerLdap ? <TileOwner ldap={ownerLdap} /> : null}
          </Box>
          {showActions ? <div className={styles.appTileActions}>{actionsMenu}</div> : null}
        </Box>
      </Box>
    );
  }

  if (listLayout) {
    const shade = withShade({
      style: {
        marginInline: -8,
        ...(launchUrl ? {cursor: 'pointer'} : {}),
      },
    });
    const rowContent = <ListCardContent app={app} />;

    const mainContent = launchUrl ? (
      <a
        aria-label={accessibleName}
        className={styles.appRowLink}
        href={launchUrl}
        onClick={(event) => {
          event.currentTarget.blur();
        }}
        rel="noopener noreferrer"
        style={{color: 'inherit'}}
        target="_blank"
      >
        {rowContent}
      </a>
    ) : (
      rowContent
    );

    return (
      <TileListRow
        shade={shade}
        trailing={showActions ? actionsMenu : undefined}
        trailingAlignY="center"
      >
        {mainContent}
      </TileListRow>
    );
  }

  const card = launchUrl ? (
    <a
      aria-label={accessibleName}
      className={styles.appRowLink}
      href={launchUrl}
      target="_blank"
      rel="noopener noreferrer"
    >
      <AppRow>
        <CompactCardContent app={app} />
      </AppRow>
    </a>
  ) : (
    <AppRow>
      <CompactCardContent app={app} />
    </AppRow>
  );

  if (!showActions) {
    return card;
  }

  return (
    <Split alignY="center">
      <Split.Item width="fill">{card}</Split.Item>
      <Split.Item>{actionsMenu}</Split.Item>
    </Split>
  );
};
